/**
 * TopperSelect — "Topper uitleg" tabs (Horizon theme)
 * Alle panelen staan in de DOM; JS toggelt zichtbaarheid, aria en focus.
 * Volgt het WAI-ARIA tabs-patroon: roving tabindex + pijltjes/Home/End.
 * Zonder JS toont de noscript-regel alle panelen onder elkaar.
 */
class TopperExplainer extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.tabs = [...this.querySelectorAll('[data-te-tab]')];
    this.panels = [...this.querySelectorAll('[data-te-panel]')];
    if (!this.tabs.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#select(index), { signal });
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index), { signal });
    });

    if (window.Shopify?.designMode) {
      // Blok geselecteerd in de theme editor: bijbehorende tab openen.
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.tabs.indexOf(event.target.closest('[data-te-tab]'));
          if (index > -1) this.#select(index, { focus: false });
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  #select(index, { focus = false } = {}) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      // Roving tabindex: alleen de actieve tab zit in de tabvolgorde.
      tab.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    if (focus) this.tabs[index].focus();
  }

  #onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(next, { focus: true });
  }
}

if (!customElements.get('topper-explainer')) {
  customElements.define('topper-explainer', TopperExplainer);
}
